import React, { useEffect, useState } from 'react';
import { CreateReportWizard } from 'src/components/auditReports/createReportPage/createReportWizard';
import { Button, Header, SpaceBetween } from '@amzn/awsui-components-react-v3';
import { Redirect } from 'react-router-dom';
import { createReportDetailLink, Page } from 'src/routes';
import { StartReportGenerationInput, StartReportGenerationOutput } from 'aws-sdk/clients/d3aservice';
import { activeGroupOrWorkspaceId } from 'src/components/dataadvisory/constants';
import { startReportGeneration } from 'src/api/d3a';
import { WIZARD_CONTENT_TYPE } from 'src/commons/constants';

export interface CreateReportPageProps {
  setNotifications: any;
  setContentType: any;
}

export const CreateReportPage = (props: CreateReportPageProps) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [redirect, setRedirect] = useState<string>(undefined);

  // Report config
  const [name, setName] = useState<string>(undefined);
  const [description, setDescription] = useState<string>(undefined);
  const [outputFormats, setOutputFormats] = useState<string[]>(['EXCEL', 'JSON']);
  const [accessProvider, setAccessProvider] = useState<string>('Omni');
  const [governanceTag, setGovernanceTag] = useState<string>(undefined);
  const [resourceArn, setResourceArn] = useState<string>(undefined);
  const [status, setStatus] = useState<string>('Active:Approved');
  const [startDate, setStartDate] = useState<string>(undefined);
  const [endDate, setEndDate] = useState<string>(undefined);

  const handleCancel = () => {
    setRedirect(Page.LIST_REPORTS);
  };

  const validate = () => {
    return !(!name || name.length == 0 || !description || description.length == 0 || outputFormats.length == 0);
  };

  const handleRefresh = () => {
    props.setContentType(WIZARD_CONTENT_TYPE);
  };

  const handleSubmit = async () => {
    setLoading(true);
    if (!validate()) {
      handleSubmitValidationError();
      setLoading(false);
      return;
    }
    let request: StartReportGenerationInput = {
      ownerId: activeGroupOrWorkspaceId(props),
      name,
      description,
      reportConfig: {
        outputTypes: outputFormats,
        accessGrantFilter: {
          accessProvider: accessProvider != undefined && accessProvider != '-' ? accessProvider : undefined,
          dgsa: governanceTag != undefined && governanceTag != '-' ? governanceTag : undefined,
          resourceArn: resourceArn?.length > 0 ? resourceArn : undefined,
          status: status != undefined && status != '-' ? status : undefined,
          // enable prefix by default for resourceARN filter if provided.
          isPrefix: !!resourceArn,
        },
      },
    };
    if (!!startDate || !!endDate) {
      request.reportConfig.accessGrantFilter.createDateRange = {
        startDate,
        endDate,
      };
    }
    try {
      const response: StartReportGenerationOutput = await startReportGeneration(request);
      if (!response.reportId) {
        handleSubmitError('Service failure!');
      }
      handleSubmitSuccess(response.reportId);
    } catch (e) {
      console.log('Error when creating report: ', e);
      const cause =
        'AccessDeniedException' === e.name
          ? 'Not authorized! Please select the resources you own using the resource ' +
            'filter during report generation!'
          : e.message;
      handleSubmitError(cause);
    }
    setLoading(false);
  };

  const handleSubmitValidationError = () => {
    props.setNotifications([
      {
        type: 'error',
        dismissible: true,
        onDismiss: () => props.setNotifications([]),
        content:
          'Failed to create the report. The following fields are required: name, description, and output formats.',
      },
    ]);
  };

  const handleSubmitError = (cause: string) => {
    props.setNotifications([
      {
        type: 'error',
        dismissible: true,
        onDismiss: () => props.setNotifications([]),
        content: `Failed to generate the report. Cause: ${cause}`,
      },
    ]);
  };

  const handleSubmitSuccess = (reportId: string) => {
    props.setNotifications([
      {
        type: 'success',
        dismissible: true,
        onDismiss: () => props.setNotifications([]),
        content: 'Successfully created the report.',
        action: (
          <Button
            onClick={() => {
              redirectToReportDetail(reportId);
              props.setNotifications([]);
            }}
          >
            View report
          </Button>
        ),
      },
    ]);
  };

  const redirectToReportDetail = (reportId: string) => {
    setRedirect(createReportDetailLink(reportId));
  };

  useEffect(() => {
    handleRefresh();
  }, []);

  if (redirect) {
    return <Redirect to={redirect} />;
  }

  return (
    <SpaceBetween size={'l'} direction={'vertical'}>
      <Header variant={'h1'}>Create report</Header>
      <CreateReportWizard
        {...props}
        name={name}
        setName={setName}
        description={description}
        setDescription={setDescription}
        outputFormats={outputFormats}
        setOutputFormats={setOutputFormats}
        accessProvider={accessProvider}
        setAccessProvider={setAccessProvider}
        governanceTag={governanceTag}
        setGovernanceTag={setGovernanceTag}
        resourceArn={resourceArn}
        setResourceArn={setResourceArn}
        status={status}
        setStatus={setStatus}
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        handleCancel={handleCancel}
        handleSubmit={handleSubmit}
        loading={loading}
      />
    </SpaceBetween>
  );
};
